import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

import styles from "./truncated-cell-tooltip.module.css";

export type TruncatedCellTooltipProps = {
  /** Full plain text shown in the tooltip when content overflows. */
  tooltipText: string;
  /** Applied to the visible (possibly ellipsized) cell content. */
  truncatedClassName: string;
  /** Defaults to overflow-only; use always for explicit hover tooltips. */
  showOnHover?: "overflow" | "always";
  children: ReactNode;
};

const VIEWPORT_PAD = 8;
const GAP = 6;

/** CSS ellipsis + portal tooltip on overflow; pattern matches `src/components/TextTooltip.jsx`. */
export function TruncatedCellTooltip({
  tooltipText,
  truncatedClassName,
  showOnHover = "overflow",
  children,
}: TruncatedCellTooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearHideTimer();
    },
    [clearHideTimer],
  );

  const isOverflowing = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return false;
    return el.scrollWidth > el.clientWidth + 1;
  }, []);

  const syncTooltipPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const tip = tooltipRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = rect.left;
    let y = rect.bottom + GAP;

    if (tip) {
      const tr = tip.getBoundingClientRect();
      const th = tr.height;
      const tw = tr.width;

      if (y + th > vh - VIEWPORT_PAD) {
        y = rect.top - th - GAP;
      }
      if (y < VIEWPORT_PAD) {
        y = VIEWPORT_PAD;
      }

      if (x + tw > vw - VIEWPORT_PAD) {
        x = vw - tw - VIEWPORT_PAD;
      }
      if (x < VIEWPORT_PAD) {
        x = VIEWPORT_PAD;
      }
    }

    const nx = Math.round(x);
    const ny = Math.round(y);
    setPos((prev) =>
      prev.x === nx && prev.y === ny ? prev : { x: nx, y: ny },
    );
  }, []);

  useLayoutEffect(() => {
    if (!show) return;
    syncTooltipPosition();
    if (tooltipRef.current == null) {
      const id = requestAnimationFrame(() => syncTooltipPosition());
      return () => cancelAnimationFrame(id);
    }
  }, [show, tooltipText, syncTooltipPosition]);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      if (show) syncTooltipPosition();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [show, syncTooltipPosition]);

  useEffect(() => {
    if (!show) return;
    const tip = tooltipRef.current;
    if (!tip || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => syncTooltipPosition());
    ro.observe(tip);
    return () => ro.disconnect();
  }, [show, syncTooltipPosition]);

  useEffect(() => {
    if (!show) return;
    const onReflow = () => syncTooltipPosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [show, syncTooltipPosition]);

  const handleEnter = () => {
    clearHideTimer();
    if (!tooltipText.trim()) return;
    if (showOnHover !== "always" && !isOverflowing()) return;
    const el = anchorRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setPos({ x: Math.round(rect.left), y: Math.round(rect.bottom + GAP) });
    }
    setShow(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShow(false);
    }, 150);
  };

  const canShowPortal = show && tooltipText.trim() !== "";

  return (
    <>
      <span
        ref={anchorRef}
        className={clsx(styles.anchor, truncatedClassName)}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        tabIndex={showOnHover === "always" && tooltipText.trim() !== "" ? 0 : undefined}
      >
        {children}
      </span>
      {canShowPortal &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={styles.portal}
            style={{ top: pos.y, left: pos.x }}
            onMouseEnter={clearHideTimer}
            onMouseLeave={handleLeave}
          >
            {tooltipText}
          </div>,
          document.body,
        )}
    </>
  );
}
